import { Linkedin, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "@/data/content";
import { INSIGHTS } from "@/data/insights";
import SectionBadge from "@/components/site/SectionBadge";
import { track } from "@/lib/analytics";

// The 4 most recently published articles, pulled from the same data source
// as /insights — new articles surface here automatically.
const LATEST = [...INSIGHTS].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 4);

export default function Insights() {
  return (
    <section
      id="insights"
      data-testid="insights-section"
      className="py-16 md:py-24 bg-[#F2F2F2]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-14">
          <div className="col-span-12 md:col-span-7">
            <SectionBadge label="The BLEZZINGS Perspective" />
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-6 leading-[1.05]">
              Ideas for leaders building the next phase of growth.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 flex md:justify-end">
            <Link
              to="/insights"
              data-testid="insights-view-all"
              onClick={() => track("insights_view_all_click", {})}
              className="group inline-flex items-center gap-3 h-12 px-6 bg-black text-white text-[15px] font-medium hover:bg-zinc-800 transition-colors duration-300"
            >
              Explore All Insights
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 reveal stagger-reveal">
          {LATEST.map((ins, i) => (
            <Link
              key={ins.slug}
              to={`/insights/${ins.slug}`}
              data-testid={`insight-card-${i}`}
              onClick={() => track("insight_card_click", { title: ins.title })}
              className="group relative bg-white border border-zinc-200 p-8 md:p-10 flex flex-col transition-all duration-500 hover:border-black hover:-translate-y-1"
            >
              {ins.image && (
                <div className="-mx-8 -mt-8 md:-mx-10 md:-mt-10 mb-6 aspect-[16/9] overflow-hidden bg-zinc-200">
                  <img
                    src={ins.image}
                    alt={ins.imageAlt || ins.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              )}
              <div className="overline">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="font-serif-display text-2xl leading-tight text-black mt-4">
                {ins.title}
              </h3>
              <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed flex-1">
                {ins.description}
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-black">
                Read the perspective
                <ArrowUpRight className="h-4 w-4 text-zinc-400 group-hover:text-black transition-colors" />
              </span>
              <span
                aria-hidden
                className="absolute left-0 bottom-0 h-[3px] w-0 bg-black transition-all duration-500 group-hover:w-full"
              />
            </Link>
          ))}
        </div>

        <a
          data-testid="insights-linkedin-cta"
          href={BRAND.contact.linkedinCompany}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("insights_linkedin_click", {})}
          className="mt-8 inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-black transition-colors"
        >
          <Linkedin className="h-4 w-4" />
          Follow BLEZZINGS on LinkedIn
        </a>
      </div>
    </section>
  );
}
